import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getPerformerCraft,
  getPlan,
  listRecordsByType,
  planHas,
  type CraftReceiptApi,
  type PerformerCraftApi,
  type RecordRow,
} from '../lib/api';
import { routePath } from '../routes/route';
import './CraftPanel.css';

// CraftPanel — W6 (`2f377423`): WHAT KEEPS COMING BACK, AND WHO SAID IT.
//
// This sits at the top of the Development tab (the pack's own "Craft" nav
// group, index.ts), above the training/awards/skills registry. It is the
// performer-level half of the craft layer; the audition-level half — one
// summary, one composer — lives on AuditionSpread.
//
// ============================================================
// ⚑ A TREND IS A LABEL, A COUNT AND ITS RECEIPTS. NOTHING ELSE.
// ============================================================
// Everything rendered here is text a coach or a parent actually wrote, quoted,
// with a door onto the note it came from. There is no advice on this screen,
// no encouragement, no "areas to work on", and there must never be: this is a
// product about somebody's child, and a sentence we composed about her would
// be a sentence her parent repeats to her.
//
// The refusal is enforced server-side (packages/core/src/craft/craftLayer.ts
// drops any quote that is not literally in the note it cites, loudly), not
// here — a client-side rule would be a hint. What this file owes the promise
// is that it renders quotes AS QUOTES and never paraphrases one into prose.
//
// ⚠ RECEIPTS ARE COLLAPSED BY DEFAULT and open on a tap. Four notes' worth of
// quotes under every trend is a wall; the trend line plus its count is the
// claim, and the receipts are how a parent checks it. Both have to be
// present — a claim with the evidence hidden behind a request is the shape
// this whole layer exists to avoid.
//
// ⚑ 2026-09-19 — READS THIS ACCOUNT'S OWN PERFORMER, TENANT-WIDE. Trends are
// cached per performer (migration 0095's `subject_type = 'performer'`), which
// used to matter across a household of several children; an account is one
// career now, so `aa_subject` never returns more than the one row and there
// is nothing left to filter by a scope.

export interface CraftPanelProps {
  tenantId: string;
  /** Opens a hash route. Optional so the panel mounts in a test without one;
   * without it a receipt still shows its words, it just has no door. */
  onNavigate?: (path: string) => void;
}

interface PerformerCraft {
  performer: RecordRow | null;
  craft: PerformerCraftApi;
}

function when(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

/** The receipt's own attribution line — who said it and when. `self` is named
 * as the family rather than as a role word, because "self" is the stored
 * value and not a thing anyone says out loud. */
function receiptWho(r: CraftReceiptApi): string {
  if (r.isSelf) return ['Your note', when(r.receivedAt)].filter(Boolean).join(' · ');
  return [r.reviewerName ?? 'A reviewer', r.reviewerRole && r.reviewerRole !== 'other' ? r.reviewerRole : null, when(r.receivedAt)]
    .filter(Boolean)
    .join(' · ');
}

export function CraftPanel({ tenantId, onNavigate }: CraftPanelProps) {
  const [on, setOn] = useState<boolean | null>(null);
  const [rows, setRows] = useState<PerformerCraft[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [say, setSay] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      let craftOn = false;
      try {
        craftOn = planHas(await getPlan(tenantId), 'craft.layer');
      } catch {
        // A plan we could not read is not a plan that includes anything. The
        // safe direction: offer nothing rather than flash a paid surface.
        craftOn = false;
      }
      if (cancelled) return;
      setOn(craftOn);
      if (!craftOn) return;

      let performers: RecordRow[] = [];
      try {
        // ⚠ aa_subject reads at 'staff'. A child or a helper gets 403 here and
        // the panel simply does not appear for them — the pack's own rule,
        // not a failure to report.
        performers = await listRecordsByType(tenantId, 'aa_subject');
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return;
        setSay('The craft layer could not be read just now.');
        return;
      }

      const loaded: PerformerCraft[] = [];
      for (const performer of performers) {
        try {
          loaded.push({ performer, craft: await getPerformerCraft(tenantId, performer.id) });
        } catch {
          // One performer's craft failing must not take the others down with
          // it; the block for that performer is simply absent.
        }
      }
      if (!cancelled) setRows(loaded);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (on === null) return null;

  // ⚠ THE TEASER IS THE WHOLE FREE-TIER SURFACE HERE. One line, honest, in the
  // place the trends would be — never a greyed-out control that errors.
  if (!on) {
    return (
      <div class="craft" data-testid="craft-panel-teaser">
        <div class="craft__sec">
          <span>Craft</span>
        </div>
        <p class="craft__say">Working finds what your coaches keep saying, and shows you the notes it came from.</p>
      </div>
    );
  }

  const withAnything = rows.filter((r) => (r.craft.trends?.items.length ?? 0) > 0 || r.craft.summaries.length > 0);

  return (
    <div class="craft" data-testid="craft-panel">
      <div class="craft__sec">
        <span>Craft</span>
      </div>

      {say ? <p class="craft__say">{say}</p> : null}

      {withAnything.length === 0 && !say ? (
        <p class="craft__say" data-testid="craft-panel-empty">
          {/* A statement of fact about what is here, not a description of what
              to do about it — the receipt floor is a real number and saying it
              is honest, while "send more tapes!" would be a caption. */}
          {rows[0]
            ? `A theme appears once ${rows[0].craft.minReceipts} separate notes say it.`
            : 'No performers on this account yet.'}
        </p>
      ) : null}

      {withAnything.map(({ performer, craft }) => (
        <div key={performer?.id ?? 'household'} data-testid="craft-performer">
          {/* Whose these are — printed only when there is more than one
              performer to tell apart, which cannot happen on this account
              (an account is one career) but is left as a guard rather than
              an assumption. */}
          {withAnything.length > 1 && performer ? (
            <div class="craft__who">{performer.display_name}</div>
          ) : null}

          {craft.trends?.items.map((trend) => {
            const key = `${performer?.id ?? ''}:${trend.label}`;
            const isOpen = open[key] ?? false;
            return (
              <div class="craft__trend" key={key} data-testid="craft-trend">
                <button
                  type="button"
                  class="craft__trend-top"
                  onClick={() => setOpen((o) => ({ ...o, [key]: !isOpen }))}
                >
                  {/* THE LABEL IS THE NOTES' OWN WORDS — the server drops any
                      trend whose label is not a phrase somebody wrote — so it
                      is set in quotation marks, because that is what it is. */}
                  <span class="craft__trend-label">&ldquo;{trend.label}&rdquo;</span>
                  <span class="craft__trend-n">
                    {trend.receipts.length} {trend.receipts.length === 1 ? 'note' : 'notes'}
                  </span>
                </button>
                {isOpen ? (
                  <div class="craft__receipts" data-testid="craft-receipts">
                    {trend.receipts.map((r) => (
                      <button
                        type="button"
                        class="craft__receipt"
                        key={r.noteId}
                        disabled={!onNavigate || !r.tapeId}
                        onClick={() =>
                          r.tapeId && onNavigate?.(routePath({ screen: 'tape', id: r.tapeId }))
                        }
                      >
                        <span class="craft__receipt-q">&ldquo;{r.quote}&rdquo;</span>
                        <span class="craft__receipt-who">{receiptWho(r)}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}

          {craft.trends?.stale ? (
            <p class="craft__say" data-testid="craft-trends-stale">
              A note has come in since these were drawn.
            </p>
          ) : null}

          {craft.summaries.length > 0 ? (
            <>
              <div class="craft__sec craft__sec--sub">
                <span>By read</span>
              </div>
              {craft.summaries.map((s) => (
                <button
                  type="button"
                  class="craft__read"
                  key={s.auditionId}
                  disabled={!onNavigate}
                  data-testid="craft-read-summary"
                  onClick={() => onNavigate?.(routePath({ screen: 'auditionDetail', id: s.auditionId }))}
                >
                  {s.lines.map((line) => (
                    <span class="craft__read-line" key={line.noteId + line.quote}>
                      &ldquo;{line.quote}&rdquo;
                      <span class="craft__receipt-who">{receiptWho(line)}</span>
                    </span>
                  ))}
                  {s.stale ? (
                    <span class="craft__receipt-who">
                      Drawn from {s.fromNoteCount} of {s.currentNoteCount} notes.
                    </span>
                  ) : null}
                </button>
              ))}
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}
